import { useEffect, useRef, useState } from 'preact/hooks';
import Plotly from 'plotly.js';
import { useTheme } from '../../contexts/ThemeContext';
import {
  colorToRgba,
  getChartColorAt,
  getChartTitleColor,
  resolveSeriesColor,
} from '../../themes/chartThemeColors';
import { normalizeCurrencyCode } from '../../functions/calculations/currencyNormalizer';
import { plotlyConfig, plotlyFontSizes, plotlyTransparentBg, sankeyChartConfig } from './config';
import '../../css/sankeyChart.css';

export interface SankeyNode {
  id: number;
  label: string;
  /** Theme color key or explicit CSS color. */
  color?: string;
}

export interface SankeyLink {
  source: number;
  target: number;
  value: number;
  /** Theme color key or explicit CSS color (optional override). */
  color?: string;
}

interface SankeyChartProps {
  nodes: SankeyNode[];
  links: SankeyLink[];
  title?: string;
  height?: number | string;
  width?: number | string;
  className?: string;
  currencyCode?: string;
}

interface HoverTooltipState {
  lines: string[];
  x: number;
  y: number;
}

function formatFlowValue(value: number, currencyCode?: string) {
  const normalizedCurrency = normalizeCurrencyCode(currencyCode);
  const formatted = Math.round(value).toLocaleString();
  return normalizedCurrency ? `${normalizedCurrency} ${formatted}` : formatted;
}

function buildHoverLines(
  point: Readonly<Plotly.PlotDatum>,
  currencyCode?: string
): string[] | null {
  const raw = point as Plotly.PlotDatum & {
    label?: string;
    source?: { label?: string };
    target?: { label?: string };
    value?: number;
  };

  if (raw.source?.label && raw.target?.label) {
    return [
      `${raw.source.label} → ${raw.target.label}`,
      `Value: ${formatFlowValue(Number(raw.value) || 0, currencyCode)}`,
    ];
  }

  if (raw.label) {
    return [
      raw.label,
      `Value: ${formatFlowValue(Number(raw.value) || 0, currencyCode)}`,
    ];
  }

  return null;
}

export const SankeyChart = ({
  nodes,
  links,
  title = '',
  height = sankeyChartConfig.defaults.height,
  width = sankeyChartConfig.defaults.width,
  className = '',
  currencyCode
}: SankeyChartProps) => {
  const { theme } = useTheme();
  const plotRef = useRef<HTMLDivElement>(null);
  const [hoverTooltip, setHoverTooltip] = useState<HoverTooltipState | null>(null);
  const cfg = sankeyChartConfig;
  const sizes = plotlyFontSizes;
  const isUnitCashflowSankey = className.includes('unit-cashflow-sankey');

  useEffect(() => {
    if (!plotRef.current || !nodes.length || !links.length) {
      return;
    }

    const plotEl = plotRef.current;
    setHoverTooltip(null);

    const resolveNodeColor = (node: SankeyNode, index: number) =>
      resolveSeriesColor(
        theme,
        node.color ?? cfg.defaultNodeColors[index % cfg.defaultNodeColors.length]
      );

    const nodeColors = nodes.map(resolveNodeColor);

    const nodeColorById = new Map(nodes.map((node, index) => [node.id, nodeColors[index]]));

    const linkColors = links.map((link) => {
      if (link.color) {
        return colorToRgba(resolveSeriesColor(theme, link.color), cfg.link.opacity);
      }
      const sourceNode = nodes.find((n) => n.id === link.source);
      const sourceIndex = sourceNode ? nodes.indexOf(sourceNode) : link.source;
      const sourceColor =
        (sourceNode && nodeColorById.get(sourceNode.id)) ||
        getChartColorAt(theme, sourceIndex);
      return colorToRgba(sourceColor, cfg.link.opacity);
    });

    const normalizedCurrency = normalizeCurrencyCode(currencyCode);
    const nodeBorderColor = resolveSeriesColor(theme, cfg.colors.nodeBorder);

    const nodeLayout = isUnitCashflowSankey
      ? { pad: 12, thickness: 18, lineWidth: cfg.node.lineWidth }
      : { pad: cfg.node.pad, thickness: cfg.node.thickness, lineWidth: cfg.node.lineWidth };

    const plotData: Plotly.Data[] = [{
      type: 'sankey',
      orientation: cfg.orientation,
      arrangement: isUnitCashflowSankey ? 'snap' : undefined,
      node: {
        ...nodeLayout,
        line: {
          color: nodeBorderColor,
          width: nodeLayout.lineWidth,
        },
        label: nodes.map((node) => node.label),
        color: nodeColors,
      },
      link: {
        source: links.map((link) => link.source),
        target: links.map((link) => link.target),
        value: links.map((link) => link.value),
        color: linkColors,
        hovertemplate:
          '<b>%{source.label}</b> → <b>%{target.label}</b><br>' +
          `Value: ${normalizedCurrency ? normalizedCurrency + ' ' : ''}%{value:,.0f}<br>` +
          '<extra></extra>',
      },
    }];

    const margins = isUnitCashflowSankey
      ? { t: 6, r: 8, b: 6, l: 8 }
      : title
        ? cfg.layout.margins.withTitle
        : cfg.layout.margins.withoutTitle;

    const layout: Partial<Plotly.Layout> = {
      title: title
        ? {
            text: title.toUpperCase(),
            font: {
              family: theme.typography.fontFamily,
              size: sizes.titleFontSize,
              color: getChartTitleColor(theme),
            },
            x: 0,
            xanchor: 'left',
            y: 1,
            yanchor: 'top',
          }
        : undefined,
      font: {
        family: theme.typography.fontFamily,
        color: resolveSeriesColor(theme, cfg.colors.text),
        size: isUnitCashflowSankey ? sizes.tickFontSize + 2 : sizes.titleFontSize,
      },
      ...plotlyTransparentBg,
      margin: { ...margins },
      autosize: true,
      width: typeof width === 'number' ? width : undefined,
      height: typeof height === 'number' ? height : undefined,
      // Hide Plotly's SVG hoverlabel for unit cashflow; we render a fixed HTML tooltip instead.
      hoverlabel: isUnitCashflowSankey
        ? {
            bgcolor: 'rgba(0,0,0,0)',
            bordercolor: 'rgba(0,0,0,0)',
            font: { color: 'rgba(0,0,0,0)', size: 1 },
          }
        : undefined,
    };

    const clearTooltip = () => setHoverTooltip(null);

    const showTooltip = (data: Readonly<Plotly.PlotMouseEvent>) => {
      if (!isUnitCashflowSankey) return;
      const point = data.points?.[0];
      if (!point) {
        clearTooltip();
        return;
      }

      const lines = buildHoverLines(point, currencyCode);
      if (!lines) {
        clearTooltip();
        return;
      }

      const event = data.event as MouseEvent | undefined;
      setHoverTooltip({
        lines,
        x: event?.clientX ?? 0,
        y: event?.clientY ?? 0,
      });
    };

    Plotly.newPlot(plotEl, plotData, layout, plotlyConfig)
      .then(() => {
        if (!isUnitCashflowSankey) return;
        plotEl.on('plotly_hover', showTooltip);
        plotEl.on('plotly_unhover', clearTooltip);
        plotEl.on('plotly_relayout', clearTooltip);
      })
      .catch((error: unknown) => {
        console.error('Error plotting sankey chart:', error);

        const errorDiv = document.createElement('div');
        const errorColor = resolveSeriesColor(theme, cfg.colors.errorText);
        errorDiv.style.cssText = `padding: 20px; text-align: center; color: ${errorColor};`;
        const errorMessage = error instanceof Error ? error.message : String(error);
        errorDiv.innerHTML = `
            <p><strong>Error rendering sankey chart</strong></p>
            <p>${errorMessage || 'Unknown error'}</p>
            <p style="font-size: 12px; margin-top: 10px;">
              Note: Sankey charts may require plotly.js (full) instead of plotly.js-basic-dist
            </p>
          `;
        plotEl.innerHTML = '';
        plotEl.appendChild(errorDiv);
      });

    const handleResize = () => {
      Plotly.Plots.resize(plotEl);
    };

    handleResize();

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(plotEl);
    const parentElement = plotEl.parentElement;
    if (parentElement) {
      resizeObserver.observe(parentElement);
    }

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    const timeoutId = window.setTimeout(handleResize, 150);

    return () => {
      window.clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      clearTooltip();
      if (isUnitCashflowSankey) {
        plotEl.removeAllListeners?.('plotly_hover');
        plotEl.removeAllListeners?.('plotly_unhover');
        plotEl.removeAllListeners?.('plotly_relayout');
      }
      Plotly.purge(plotEl);
    };
  }, [nodes, links, title, height, width, currencyCode, theme, className, isUnitCashflowSankey]);

  if (!nodes || nodes.length === 0 || !links || links.length === 0) {
    return (
      <div className={`sankey-chart-container ${className}`}>
        <div className="sankey-chart-empty">
          <p>{cfg.emptyMessage}</p>
        </div>
      </div>
    );
  }

  const tooltipOffset = 14;
  const tooltipStyle = hoverTooltip
    ? (() => {
        const approxWidth = 200;
        const approxHeight = 72;
        const left =
          hoverTooltip.x + tooltipOffset + approxWidth > window.innerWidth
            ? Math.max(8, hoverTooltip.x - approxWidth - tooltipOffset)
            : hoverTooltip.x + tooltipOffset;
        const top =
          hoverTooltip.y + tooltipOffset + approxHeight > window.innerHeight
            ? Math.max(8, hoverTooltip.y - approxHeight - tooltipOffset)
            : hoverTooltip.y + tooltipOffset;
        return { left: `${left}px`, top: `${top}px` };
      })()
    : undefined;

  return (
    <div className={`sankey-chart-container ${className}`}>
      <div ref={plotRef} style={{ width: '100%', height: '100%' }} />
      {isUnitCashflowSankey && hoverTooltip && (
        <div className="sankey-hover-tooltip" style={tooltipStyle} role="tooltip">
          {hoverTooltip.lines.map((line) => (
            <div key={line} className="sankey-hover-tooltip__line">
              {line}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
